<template>
  <div class="heart-center">
    <!-- 爱心容器 -->
    <div class="heart-container" @click="handleHeartClick">
      <!-- 外层波纹 -->
      <div
        v-for="(ripple, index) in activeRipples"
        :key="ripple.id"
        class="heart-ripple"
        :style="getRippleStyle(ripple, index)"
      ></div>

      <!-- 爱心主体 -->
      <div
        class="heart"
        :class="getHeartClass()"
        ref="heart"
      >
        <div class="heart-inner">
          <!-- 苹果风格的渐变爱心 -->
          <svg viewBox="0 0 100 100" class="heart-svg">
            <defs>
              <linearGradient id="heartGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" :style="`stop-color:${getHeartColor(0)};stop-opacity:1`" />
                <stop offset="50%" :style="`stop-color:${getHeartColor(0.5)};stop-opacity:1`" />
                <stop offset="100%" :style="`stop-color:${getHeartColor(1)};stop-opacity:1`" />
              </linearGradient>

              <!-- 高光效果 -->
              <radialGradient id="heartHighlight" cx="30%" cy="30%">
                <stop offset="0%" style="stop-color:rgba(255,255,255,0.4);stop-opacity:1" />
                <stop offset="100%" style="stop-color:rgba(255,255,255,0);stop-opacity:0" />
              </radialGradient>
            </defs>

            <!-- 爱心路径 -->
            <path
              d="M50,85 C50,85 20,65 20,40 C20,25 30,15 45,15 C50,20 55,20 55,15 C70,15 80,25 80,40 C80,65 50,85 50,85 Z"
              fill="url(#heartGradient)"
              class="heart-path"
            />

            <!-- 高光层 -->
            <ellipse cx="38" cy="35" rx="12" ry="8" fill="url(#heartHighlight)" />
          </svg>
        </div>
      </div>

      <!-- 点击反馈 -->
      <div v-if="showClickFeedback" class="click-feedback"></div>
    </div>

    <!-- 状态提示 -->
    <div class="heart-status">
      <span class="status-text">{{ getStatusText() }}</span>
    </div>

    <!-- 💫 磨砂玻璃点击提示 -->
    <HeartClickToast ref="heartToast" />
  </div>
</template>

<script>
import HeartClickToast from '@/components/HeartClickToast.vue'

export default {
  name: 'HeartCenter',
  components: {
    HeartClickToast
  },
  props: {
    receivedMissIntensity: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      // 动画状态
      isBeating: false,
      beatIntensity: 0,

      // 波纹效果
      activeRipples: [],
      rippleIdCounter: 0,

      // 交互反馈
      showClickFeedback: false,

      // 呼吸动画
      breathePhase: 0,
      breatheTimer: null,

      // 跳动动画
      beatTimer: null,

      // 自定义想念强度（用于发送时的连击效果）
      sendMissBuffer: [],
      lastSendTime: 0,

      // 状态清理定时器
      stateCleanupTimer: null
    }
  },
  computed: {
    // 当前爱心状态
    heartState () {
      if (this.receivedMissIntensity > 0) {
        return 'receiving' // 收到想念状态
      }
      return 'normal' // 正常状态
    },

    // 当前发送强度等级
    currentSendIntensity () {
      const recentSends = this.sendMissBuffer.length
      if (recentSends <= 1) return 0
      if (recentSends <= 3) return 1
      if (recentSends <= 6) return 2
      return 3
    }
  },
  mounted () {
    this.startBreathing()
    this.updateBeatAnimation()
    this.loadSendMissState() // 🎯 恢复发送思念状态
    this.startStateCleanupTimer() // 🎯 启动状态清理定时器
  },
  beforeDestroy () {
    this.stopAllAnimations()
    this.stopStateCleanupTimer() // 🎯 清理状态定时器
  },
  watch: {
    receivedMissIntensity (newVal, oldVal) {
      if (newVal > oldVal && newVal > 0) {
        // 收到新的想念，触发跳动
        this.triggerBeat()
        this.spawnRipple()
      }
      this.updateBeatAnimation()
    }
  },
  methods: {
    // 处理爱心点击
    handleHeartClick () {
      if (this.receivedMissIntensity > 0) {
        // 有未读想念，点击表示"收到了"
        this.handleReceiveMiss()
      } else {
        // 没有未读想念，点击表示"想你"
        this.handleSendMiss()
      }

      // 显示点击反馈
      this.showClickFeedback = true
      setTimeout(() => { this.showClickFeedback = false }, 300)

      // 触发波纹
      this.spawnRipple()
    },

    // 处理发送想念
    handleSendMiss () {
      const now = Date.now()

      // 管理发送缓冲区（保留5分钟内的点击）
      this.sendMissBuffer.push(now)
      this.sendMissBuffer = this.sendMissBuffer.filter(time => now - time < 300000)

      this.lastSendTime = now

      // 🎯 持久化保存发送状态
      this.saveSendMissState()

      // 发送事件
      this.$emit('miss-expressed', {
        intensity: 1,
        timestamp: now,
        currentLevel: this.currentSendIntensity
      })

      // 显示反馈
      this.$refs.heartToast.showSendToast(this.currentSendIntensity)
    },

    // 处理接收想念
    handleReceiveMiss () {
      const clearAmount = 1
      const previousIntensity = this.receivedMissIntensity
      const newIntensity = Math.max(0, this.receivedMissIntensity - clearAmount)
      const isComplete = newIntensity === 0

      // 发送事件
      this.$emit('miss-received', {
        clearAmount,
        newIntensity,
        isComplete,
        previousIntensity
      })

      // 显示反馈
      this.$refs.heartToast.showReceiveToast(newIntensity, isComplete)
    },

    // 开始呼吸动画
    startBreathing () {
      this.breatheTimer = setInterval(() => {
        this.breathePhase += 0.05
        if (this.breathePhase >= Math.PI * 2) {
          this.breathePhase = 0
        }
      }, 50)
    },

    // 更新跳动动画
    updateBeatAnimation () {
      if (this.beatTimer) {
        clearInterval(this.beatTimer)
        this.beatTimer = null
      }

      if (this.receivedMissIntensity > 0) {
        // 根据想念强度调整跳动频率
        const baseInterval = 1000 // 基础1秒
        const intensity = Math.min(this.receivedMissIntensity / 100, 1) // 0-1
        const interval = baseInterval - (intensity * 600) // 最快400ms

        this.beatIntensity = intensity
        this.isBeating = true

        this.beatTimer = setInterval(() => {
          this.triggerBeat()
        }, interval)
      } else {
        this.isBeating = false
        this.beatIntensity = 0
      }
    },

    // 触发跳动
    triggerBeat () {
      if (this.$refs.heart) {
        this.$refs.heart.style.animation = 'none'
        setTimeout(() => {
          this.$refs.heart.style.animation = ''
        }, 10)
      }
    },

    // 生成波纹
    spawnRipple () {
      const ripple = {
        id: this.rippleIdCounter++,
        timestamp: Date.now(),
        intensity: this.heartState === 'receiving' ? this.beatIntensity : 0.3
      }

      this.activeRipples.push(ripple)

      // 3秒后移除
      setTimeout(() => {
        const index = this.activeRipples.findIndex(r => r.id === ripple.id)
        if (index > -1) {
          this.activeRipples.splice(index, 1)
        }
      }, 3000)
    },

    // 停止所有动画
    stopAllAnimations () {
      if (this.breatheTimer) clearInterval(this.breatheTimer)
      if (this.beatTimer) clearInterval(this.beatTimer)
      this.stopStateCleanupTimer()
    },

    // 获取爱心CSS类
    getHeartClass () {
      const classes = []

      if (this.heartState === 'receiving') {
        classes.push('heart-receiving')
        if (this.beatIntensity > 0.3) classes.push('heart-intense')
        if (this.beatIntensity > 0.6) classes.push('heart-extreme')
      } else {
        classes.push('heart-normal')
        if (this.currentSendIntensity > 0) classes.push(`heart-send-${this.currentSendIntensity}`)
      }

      return classes
    },

    // 获取爱心颜色
    getHeartColor (position) {
      if (this.heartState === 'receiving') {
        // 收到想念时的颜色（粉红到红色）
        const intensity = this.beatIntensity
        if (intensity < 0.3) {
          return position === 0 ? '#FF6B9D' : '#F06292'
        } else if (intensity < 0.6) {
          return position === 0 ? '#FF4081' : '#E91E63'
        } else {
          return position === 0 ? '#FF1744' : '#D32F2F'
        }
      } else {
        // 正常/发送状态（蓝色到紫色）
        const sendLevel = this.currentSendIntensity
        switch (sendLevel) {
          case 0:
            return position === 0 ? '#667eea' : '#764ba2'
          case 1:
            return position === 0 ? '#8B5CF6' : '#A855F7'
          case 2:
            return position === 0 ? '#EC4899' : '#BE185D'
          default:
            return position === 0 ? '#EF4444' : '#DC2626'
        }
      }
    },

    // 获取波纹样式
    getRippleStyle (ripple, index) {
      const elapsed = Date.now() - ripple.timestamp
      const progress = Math.min(elapsed / 3000, 1) // 3秒完成
      const scale = 1 + progress * 3 // 从1到4倍大小
      const opacity = (1 - progress) * 0.3 * ripple.intensity

      return {
        transform: `scale(${scale})`,
        opacity: opacity,
        animationDelay: `${index * 0.1}s`
      }
    },

    // 获取状态文本
    getStatusText () {
      if (this.receivedMissIntensity > 0) {
        if (this.receivedMissIntensity === 1) {
          return '轻触确认收到思念'
        } else {
          return `轻触确认收到思念 (${this.receivedMissIntensity})`
        }
      } else {
        if (this.currentSendIntensity !== 0) {
          return '思念在心中荡漾'
        }
      }
    },

    // 🎯 保存发送思念状态
    saveSendMissState () {
      const state = {
        sendMissBuffer: this.sendMissBuffer,
        lastSendTime: this.lastSendTime
      }
      localStorage.setItem('heartSendMissState', JSON.stringify(state))
    },

    // 🎯 加载发送思念状态
    loadSendMissState () {
      try {
        const savedState = localStorage.getItem('heartSendMissState')
        if (savedState) {
          const state = JSON.parse(savedState)
          const now = Date.now()

          // 过滤掉过期的点击（5分钟外的）
          this.sendMissBuffer = (state.sendMissBuffer || [])
            .filter(time => now - time < 300000)

          this.lastSendTime = state.lastSendTime || 0

          // 如果状态有变化，保存清理后的状态
          if (this.sendMissBuffer.length !== (state.sendMissBuffer || []).length) {
            this.saveSendMissState()
          }
        }
      } catch (error) {
        console.error('加载发送思念状态失败:', error)
        this.sendMissBuffer = []
        this.lastSendTime = 0
      }
    },

    // 🎯 处理对方确认收到思念（减少发送强度）
    handleMissConfirmed () {
      if (this.sendMissBuffer.length > 0) {
        // 移除一个发送记录，模拟强度降低
        this.sendMissBuffer.pop()
        this.saveSendMissState()

        // 如果完全清空了，显示特殊提示
        if (this.sendMissBuffer.length === 0) {
          this.$refs.heartToast.createBubble('思念传达成功', '✨', 'receive')
        }
      }
    },

    // 🎯 启动状态清理定时器
    startStateCleanupTimer () {
      // 每30秒检查一次过期的发送记录
      this.stateCleanupTimer = setInterval(() => {
        const now = Date.now()
        const oldLength = this.sendMissBuffer.length

        // 清理5分钟外的记录
        this.sendMissBuffer = this.sendMissBuffer.filter(time => now - time < 300000)

        // 如果有变化，保存状态
        if (this.sendMissBuffer.length !== oldLength) {
          this.saveSendMissState()
          console.log(`爱心状态清理: ${oldLength} → ${this.sendMissBuffer.length}`)
        }
      }, 30000) // 30秒检查一次
    },

    // 🎯 停止状态清理定时器
    stopStateCleanupTimer () {
      if (this.stateCleanupTimer) {
        clearInterval(this.stateCleanupTimer)
        this.stateCleanupTimer = null
      }
    }

  }
}
</script>

<style scoped>
/* 🎯 苹果风格的爱心中心 */
.heart-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 10px; /* 减少内边距 */
  margin-bottom: 12px; /* 减少下边距 */
}

/* 💖 爱心容器 */
.heart-container {
  position: relative;
  width: 120px;
  height: 120px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

/* 💝 爱心主体 */
.heart {
  width: 80px;
  height: 80px;
  position: relative;
  z-index: 2;
  transform-origin: center center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
}

.heart-inner {
  width: 100%;
  height: 100%;
  transform-origin: center center;
  animation: breathe 4s ease-in-out infinite;
}

.heart-svg {
  width: 100%;
  height: 100%;
}

.heart-path {
  transform-origin: center center;
  transition: all 0.2s ease;
}

/* 🎨 爱心渐变过渡动画 */
#heartGradient stop {
  transition: stop-color 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* 🎭 爱心状态样式 */
.heart-normal .heart-inner {
  animation: breathe 4s ease-in-out infinite;
}

.heart-receiving .heart-inner {
  animation: heartbeat 0.8s ease-in-out infinite;
}

.heart-receiving.heart-intense .heart-inner {
  animation: heartbeat-intense 0.6s ease-in-out infinite;
}

.heart-receiving.heart-extreme .heart-inner {
  animation: heartbeat-extreme 0.4s ease-in-out infinite;
}

/* 发送状态样式 */
.heart-send-1 {
  filter: drop-shadow(0 4px 12px rgba(139, 92, 246, 0.3));
}

.heart-send-2 {
  filter: drop-shadow(0 4px 16px rgba(236, 72, 153, 0.4));
}

.heart-send-3 {
  filter: drop-shadow(0 6px 20px rgba(239, 68, 68, 0.5));
  animation: pulse-glow 1s ease-in-out infinite;
}

/* 🌊 波纹效果 */
.heart-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid rgba(255, 107, 157, 0.3);
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
  animation: ripple-expand 3s ease-out forwards;
}

/* 💫 点击反馈 */
.click-feedback {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 100%
  );
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 3;
  animation: click-pulse 0.3s ease-out;
}

/* 📝 状态文本 */
.heart-status {
  text-align: center;
  margin-top: 8px; /* 增加上边距，确保可见 */
  min-height: 20px; /* 确保有足够高度 */
}

.status-text {
  font-size: 0.85rem;
  color: #6B7280;
  font-weight: 500;
  letter-spacing: 0.3px;
  opacity: 0.9; /* 增加透明度，提高可见性 */
  line-height: 1.4;
}

/* 🎬 动画定义 */
@keyframes breathe {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes heartbeat {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

@keyframes heartbeat-intense {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.25);
  }
}

@keyframes heartbeat-extreme {
  0%, 100% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.1);
  }
  50% {
    transform: scale(1.3);
  }
  75% {
    transform: scale(1.1);
  }
}

@keyframes pulse-glow {
  0%, 100% {
    filter: drop-shadow(0 6px 20px rgba(239, 68, 68, 0.5));
  }
  50% {
    filter: drop-shadow(0 8px 28px rgba(239, 68, 68, 0.8));
  }
}

@keyframes ripple-expand {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(3);
    opacity: 0;
  }
}

@keyframes click-pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0;
  }
}

/* 📱 移动端优化 */
@media (max-width: 600px) {
  .heart-center {
    padding: 16px 10px; /* 进一步减少移动端内边距 */
    margin-bottom: 8px; /* 减少移动端下边距 */
  }

  .heart-container {
    width: 100px;
    height: 100px;
    margin-bottom: 12px;
  }

  .heart {
    width: 70px;
    height: 70px;
  }

  .heart-ripple {
    width: 70px;
    height: 70px;
  }

  .heart-status {
    margin-top: 6px; /* 移动端状态文字上边距 */
  }

  .status-text {
    font-size: 0.8rem;
    opacity: 1; /* 移动端最大可见性 */
  }
}

/* 💫 交互增强 */
.heart-container:active .heart {
  transform: scale(0.95);
  transition: transform 0.1s ease;
}

.heart-container:hover .heart {
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.2));
}

/* 🌈 高性能GPU加速 */
.heart, .heart-inner, .heart-ripple, .click-feedback {
  will-change: transform;
  backface-visibility: hidden;
  transform: translateZ(0);
}
</style>
